/* eslint-disable react-hooks/rules-of-hooks */
import {
  Box,
  Editor,
  Rectangle2d,
  ShapeUtil,
  SvgExportContext,
  TLGeometryOpts,
  TLPageId,
  TLResizeInfo,
  TLRichText,
  TLShapeId,
  TLTextShape,
  TLUnknownShape,
  Vec,
  createComputedCache,
  getDefaultColorTheme,
  getFontsFromRichText,
  resizeScaled,
  toDomPrecision,
  toRichText,
  useEditor,
} from "tldraw"
import { useCallback } from "react"
import {
  FONT_FAMILIES,
  FONT_SIZES,
  TEXT_PROPS,
} from "../shared/default-shape-constants"
import {
  LDTextShape,
  textShapeMigrations,
  textShapeProps,
} from "../../shared/LDTextShape"
import {
  renderHtmlFromRichTextForMeasurement,
  renderPlaintextFromRichText,
  RichTextLabel,
  RichTextSVG,
  useDefaultColorTheme,
} from "tldraw"
import { isEqual } from "lodash"
import { LiveDraw } from "../LiveDraw"
import { LDPage } from "../../shared/LDPage"
import { Expr } from "../../shared/Expr"
import { LDInstanceMeta } from "../../shared/LDInstance"
import { LDGroupShape, LDShape } from "../../shared/LDShape"
import { flattenParams } from "./flattenParams"

const sizeCache = createComputedCache(
  "text size",
  (editor: Editor, shape: LDTextShape) => {
    editor.fonts.trackFontsForShape(shape)
    return getTextSize(editor, shape)
  },
  { areRecordsEqual: (a, b) => a.props === b.props },
)
/** @public */
export interface TextShapeOptions {
  /** How much addition padding should be added to the horizontal geometry of the shape when binding to an arrow? */
  extraArrowHorizontalPadding: number
}

/** @public */
export class LDTextShapeUtil extends ShapeUtil<LDTextShape> {
  static override type = "text2" as const
  static override props = textShapeProps
  static override migrations = textShapeMigrations

  override options: TextShapeOptions = {
    extraArrowHorizontalPadding: 10,
  }

  getDefaultProps(): LDTextShape["props"] {
    return {
      size: "m",
      w: 100,
      textAlign: "start",
      autoSize: true,
      scale: 1,
      richText: toRichText(""),
      layer: "layer1",
      parameterValues: {
        strokeColor: Expr.lit(
          (this.editor.getInstanceState().meta as LDInstanceMeta).strokeColor,
        ),
        text: Expr.lit(""),
        visible: Expr.lit(true),
        font: Expr.lit("draw"),
        opacity: Expr.lit(1),
      },
    }
  }

  getMinDimensions(shape: LDTextShape) {
    return sizeCache.get(this.editor, shape.id)!
  }

  getGeometry(shape: LDTextShape, opts: TLGeometryOpts) {
    const { scale } = shape.props
    const { width, height } = this.getMinDimensions(shape)!
    const context = opts?.context ?? "none"
    return new Rectangle2d({
      x:
        (context === "@tldraw/arrow-without-arrowhead"
          ? -this.options.extraArrowHorizontalPadding
          : 0) * scale,
      width:
        (width +
          (context === "@tldraw/arrow-without-arrowhead"
            ? this.options.extraArrowHorizontalPadding * 2
            : 0)) *
        scale,
      height: height * scale,
      isFilled: true,
      isLabel: true,
    })
  }

  override getFontFaces(shape: LDTextShape) {
    const params = flattenParams(shape, this.editor)
    // no need for an empty rich text check here
    return getFontsFromRichText(this.editor, shape.props.richText, {
      family: `tldraw_${Expr.evaluate(shape.props.parameterValues.font, params)}`,
      weight: "normal",
      style: "normal",
    })
  }

  override getText(shape: LDTextShape) {
    return renderPlaintextFromRichText(this.editor, shape.props.richText)
  }

  override canEdit() {
    return true
  }

  override isAspectRatioLocked() {
    return true
  } // WAIT NO THIS IS HARD CODED IN THE RESIZE HANDLER

  component(shape: LDTextShape) {
    const {
      id,
      props: { size, scale, textAlign },
    } = shape

    const { width, height } = this.getMinDimensions(shape)
    const isSelected = shape.id === this.editor.getOnlySelectedShapeId()
    const handleKeyDown = useTextShapeKeydownHandler(id)
    const sourcePageId = LDShape.getSourcePageId(this.editor, shape)
    const page = this.editor.getPage(sourcePageId) as LDPage

    const layer = page.meta.layers[shape.props.layer]

    const params = flattenParams(shape, this.editor)

    const richText =
      Expr.evaluate(shape.props.parameterValues.text, params).length > 0
        ? toRichText(Expr.evaluate(shape.props.parameterValues.text, params))
        : shape.props.richText

    const color = Expr.evaluate(shape.props.parameterValues.strokeColor, params)

    const font = Expr.evaluate(shape.props.parameterValues.font, params)

    const pageMeta = page.meta

    const visible =
      Expr.evaluate(layer.visible, params) &&
      Expr.evaluate(shape.props.parameterValues.visible, params)

    const opacity =
      Expr.evaluate(layer.opacity, params) *
      Expr.evaluate(shape.props.parameterValues.opacity, params)

    const adjustedOpacity = visible
      ? !pageMeta.focusedLayerId
        ? opacity
        : pageMeta.focusedLayerId === shape.props.layer
          ? opacity + (1 - opacity) / 2
          : opacity * 0.3
      : 0

    return (
      <RichTextLabel
        shapeId={id}
        classNamePrefix="tl-text-shape"
        type="text"
        font={font}
        fontSize={FONT_SIZES[size]}
        lineHeight={TEXT_PROPS.lineHeight}
        align={textAlign}
        verticalAlign="middle"
        richText={richText}
        labelColor={color}
        isSelected={isSelected}
        textWidth={width}
        textHeight={height}
        style={{
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          opacity: adjustedOpacity,
        }}
        wrap
        onKeyDown={handleKeyDown}
      />
    )
  }

  indicator(shape: LDTextShape) {
    const bounds = this.editor.getShapeGeometry(shape).bounds
    const editor = useEditor()
    if (shape.props.autoSize && editor.getEditingShapeId() === shape.id)
      return null
    return (
      <rect
        width={toDomPrecision(bounds.width)}
        height={toDomPrecision(bounds.height)}
      />
    )
  }

  override toSvg(shape: LDTextShape, ctx: SvgExportContext) {
    const bounds = this.editor.getShapeGeometry(shape).bounds
    const width = bounds.width / (shape.props.scale ?? 1)
    const height = bounds.height / (shape.props.scale ?? 1)

    const theme = getDefaultColorTheme(ctx)

    const exportBounds = new Box(0, 0, width, height)

    const params = flattenParams(shape, this.editor)

    const richText =
      Expr.evaluate(shape.props.parameterValues.text, params).length > 0
        ? toRichText(Expr.evaluate(shape.props.parameterValues.text, params))
        : shape.props.richText

    const color = Expr.evaluate(shape.props.parameterValues.strokeColor, params)
    const font = Expr.evaluate(shape.props.parameterValues.font, params)
    return (
      <RichTextSVG
        fontSize={FONT_SIZES[shape.props.size]}
        font={font}
        align={shape.props.textAlign}
        verticalAlign="middle"
        richText={richText}
        labelColor={color}
        bounds={exportBounds}
        padding={0}
      />
    )
  }

  override onResize(shape: LDTextShape, info: TLResizeInfo<LDTextShape>) {
    const { newPoint, initialBounds, initialShape, scaleX, handle } = info

    if (
      info.mode === "scale_shape" ||
      (handle !== "right" && handle !== "left")
    ) {
      return {
        id: shape.id,
        type: shape.type,
        ...resizeScaled(shape, info),
      }
    } else {
      const nextWidth = Math.max(1, Math.abs(initialBounds.width * scaleX))
      const { x, y } =
        scaleX < 0
          ? Vec.Sub(newPoint, Vec.FromAngle(shape.rotation).mul(nextWidth))
          : newPoint

      return {
        id: shape.id,
        type: shape.type,
        x,
        y,
        props: {
          w: nextWidth / initialShape.props.scale,
          autoSize: false,
        },
      }
    }
  }

  override onEditEnd(shape: LDTextShape) {
    // todo: find a way to check if the rich text has any nodes that aren't empty spaces
    const trimmedText = renderPlaintextFromRichText(
      this.editor,
      shape.props.richText,
    ).trimEnd()

    if (trimmedText.length === 0) {
      this.editor.deleteShapes([shape.id])
    }
  }

  override onBeforeUpdate(prev: LDTextShape, next: LDTextShape) {
    if (!next.props.autoSize) return

    const params = flattenParams(next, this.editor)

    const styleDidChange =
      prev.props.size !== next.props.size ||
      prev.props.textAlign !== next.props.textAlign ||
      Expr.evaluate(prev.props.parameterValues.font, params) !==
        Expr.evaluate(next.props.parameterValues.font, params) ||
      (prev.props.scale !== 1 && next.props.scale === 1)

    const textDidChange = !isEqual(prev.props.richText, next.props.richText)

    // Only update position if either changed
    if (!styleDidChange && !textDidChange) return

    // Might return a cached value for the bounds
    const boundsA = this.getMinDimensions(prev)

    // Will always be a fresh call to getTextSize
    // alvaro: we override the color; we don't use TL colors, and getTextSize doesn't need ours
    const boundsB = getTextSize(this.editor, {
      ...next,
      // color: "black",
      // font: Expr.evaluate(next.props.parameterValues.font, params),
    })

    const wA = boundsA.width * prev.props.scale
    const hA = boundsA.height * prev.props.scale
    const wB = boundsB.width * next.props.scale
    const hB = boundsB.height * next.props.scale

    let delta: Vec | undefined

    switch (next.props.textAlign) {
      case "middle": {
        delta = new Vec((wB - wA) / 2, textDidChange ? 0 : (hB - hA) / 2)
        break
      }
      case "end": {
        delta = new Vec(wB - wA, textDidChange ? 0 : (hB - hA) / 2)
        break
      }
      default: {
        if (textDidChange) break
        delta = new Vec(0, (hB - hA) / 2)
        break
      }
    }

    if (delta) {
      // account for shape rotation when writing text:
      delta.rot(next.rotation)
      const { x, y } = next
      return {
        ...next,
        x: x - delta.x,
        y: y - delta.y,
        props: { ...next.props, w: wB },
      }
    } else {
      return {
        ...next,
        props: { ...next.props, w: wB },
      }
    }
  }

  // 	todo: The edge doubleclicking feels like a mistake more often than
  //  not, especially on multiline text. Removed June 16 2024

  // override onDoubleClickEdge = (shape: TLTextShape) => {
  // 	// If the shape has a fixed width, set it to autoSize.
  // 	if (!shape.props.autoSize) {
  // 		return {
  // 			id: shape.id,
  // 			type: shape.type,
  // 			props: {
  // 				autoSize: true,
  // 			},
  // 		}
  // 	}
  // 	// If the shape is scaled, reset the scale to 1.
  // 	if (shape.props.scale !== 1) {
  // 		return {
  // 			id: shape.id,
  // 			type: shape.type,
  // 			props: {
  // 				scale: 1,
  // 			},
  // 		}
  // 	}
  // }
}

function getTextSize(editor: Editor, shape: LDTextShape) {
  console.log({ shape })
  const props = shape.props
  console.log({ props })
  const { richText, autoSize, size, w } = props
  const font = Expr.evaluate(
    props.parameterValues.font,
    flattenParams(shape, editor),
  )

  const minWidth = autoSize ? 16 : Math.max(16, w)
  const fontSize = FONT_SIZES[size]

  const cw = autoSize
    ? null
    : // `measureText` floors the number so we need to do the same here to avoid issues.
      Math.floor(Math.max(minWidth, w))

  const html = renderHtmlFromRichTextForMeasurement(editor, richText)
  const result = editor.textMeasure.measureHtml(html, {
    ...TEXT_PROPS,
    fontFamily: FONT_FAMILIES[font],
    fontSize: fontSize,
    maxWidth: cw,
  })

  // If we're autosizing the measureText will essentially `Math.floor`
  // the numbers so `19` rather than `19.3`, this means we must +1 to
  // whatever we get to avoid wrapping.
  if (autoSize) {
    result.w += 1
  }

  return {
    width: Math.max(minWidth, result.w),
    height: Math.max(fontSize, result.h),
  }
}

function useTextShapeKeydownHandler(id: TLShapeId) {
  const editor = useEditor()

  return useCallback(
    (e: KeyboardEvent) => {
      if (editor.getEditingShapeId() !== id) return

      switch (e.key) {
        case "Enter": {
          if (e.ctrlKey || e.metaKey) {
            editor.complete()
          }
          break
        }
      }
    },
    [editor, id],
  )
}
